import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { getMangaById, allManga } from '@/mocks/manga';
import { getChaptersByMangaId, getChapterContent } from '@/mocks/chapters';
import { categories } from '@/mocks/categories';
import type { Manga } from '@/mocks/manga';
import type { Chapter, ChapterContent } from '@/mocks/chapters';

export default function ArticlePage() {
  const { mangaId } = useParams<{ mangaId: string }>();
  const [manga, setManga] = useState<Manga | undefined>(undefined);
  const [chapterList, setChapterList] = useState<Chapter[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [chapterContent, setChapterContent] = useState<ChapterContent | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [showChapterList, setShowChapterList] = useState(false);
  const readingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mangaId) {
      const found = getMangaById(mangaId);
      setManga(found);
      if (found) {
        const chapters = getChaptersByMangaId(found.id);
        setChapterList(chapters);
      }
    }
    setIsReading(false);
    setSelectedChapter(null);
    setChapterContent(null);
  }, [mangaId]);

  const handleStartReading = () => {
    if (chapterList.length > 0) {
      const latestChapter = chapterList[0];
      setSelectedChapter(latestChapter);
      const content = getChapterContent(latestChapter.id);
      setChapterContent(content);
      setIsReading(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSelectChapter = (chapter: Chapter) => {
    setSelectedChapter(chapter);
    const content = getChapterContent(chapter.id);
    setChapterContent(content);
    setShowChapterList(false);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handlePrevChapter = () => {
    if (!selectedChapter) return;
    const idx = chapterList.findIndex((c) => c.id === selectedChapter.id);
    if (idx < chapterList.length - 1) {
      handleSelectChapter(chapterList[idx + 1]);
    }
  };

  const handleNextChapter = () => {
    if (!selectedChapter) return;
    const idx = chapterList.findIndex((c) => c.id === selectedChapter.id);
    if (idx > 0) {
      handleSelectChapter(chapterList[idx - 1]);
    }
  };

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </div>
          <p className="mt-4 text-foreground-500">未找到该漫画</p>
          <Link
            to="/"
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const mangaCategories = categories.filter((c) => manga.categories.includes(c.id));
  const relatedManga = allManga
    .filter((m) => m.id !== manga.id && m.categories.some((c) => manga.categories.includes(c)))
    .slice(0, 6);
  const currentChapterIdx = selectedChapter
    ? chapterList.findIndex((c) => c.id === selectedChapter.id)
    : -1;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: mangaCategories[0]?.name || '漫画', href: `/category/${mangaCategories[0]?.slug || 'school-life'}` },
    { label: manga.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: manga.title,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    description: manga.description,
    url: `http://91cghlw.cn/article/${manga.id}`,
    inLanguage: 'zh-CN',
    datePublished: '2025-03-15',
    dateModified: manga.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '178漫画网',
      url: 'http://91cghlw.cn/',
    },
    genre: mangaCategories.map((c) => c.name),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {isReading && chapterContent ? (
          <main ref={readingRef} className="bg-foreground-950">
            {/* Reading Header */}
            <div className="sticky top-16 z-30 bg-foreground-900 border-b border-foreground-800">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-4xl mx-auto flex items-center justify-between h-12">
                  <button
                    onClick={() => setIsReading(false)}
                    className="flex items-center gap-1 text-sm text-foreground-400 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回详情
                  </button>
                  <div className="text-center">
                    <h2 className="text-sm font-medium text-background-100 truncate max-w-[200px] md:max-w-xs">
                      {manga.title} - {selectedChapter?.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowChapterList(!showChapterList)}
                    className="flex items-center gap-1 text-sm text-foreground-400 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-list-unordered"></i>
                    目录
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List Dropdown */}
            {showChapterList && (
              <div className="fixed inset-0 top-[112px] z-20 bg-foreground-950/95 overflow-y-auto">
                <div className="max-w-lg mx-auto p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-background-100">章节列表</h3>
                    <button
                      onClick={() => setShowChapterList(false)}
                      className="text-foreground-400 hover:text-background-50 cursor-pointer"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                  <div className="space-y-1">
                    {chapterList.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => handleSelectChapter(ch)}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          selectedChapter?.id === ch.id
                            ? 'bg-primary-600 text-white'
                            : 'text-foreground-300 hover:bg-foreground-800'
                        }`}
                      >
                        <span className="font-medium">{ch.title}</span>
                        <span className="text-xs opacity-60 ml-2">{ch.updateDate}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Reading Content */}
            <div className="max-w-2xl mx-auto">
              {chapterContent.images.map((img, idx) => (
                <div key={idx} className="w-full">
                  <img
                    src={img}
                    alt={`${manga.title} ${selectedChapter?.title} 第${idx + 1}页`}
                    title={`${manga.title} ${selectedChapter?.title} - 178漫画网`}
                    className="w-full h-auto block"
                    loading={idx < 3 ? 'eager' : 'lazy'}
                  />
                </div>
              ))}

              {/* Navigation */}
              <div className="flex items-center justify-between py-8 px-4">
                <button
                  onClick={handlePrevChapter}
                  disabled={currentChapterIdx >= chapterList.length - 1}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    currentChapterIdx >= chapterList.length - 1
                      ? 'bg-foreground-800 text-foreground-600 cursor-not-allowed'
                      : 'bg-foreground-800 text-background-100 hover:bg-foreground-700'
                  }`}
                >
                  <i className="ri-arrow-left-line"></i>
                  上一话
                </button>
                <span className="text-sm text-foreground-400">
                  {selectedChapter?.title}
                </span>
                <button
                  onClick={handleNextChapter}
                  disabled={currentChapterIdx <= 0}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    currentChapterIdx <= 0
                      ? 'bg-foreground-800 text-foreground-600 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  下一话
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </main>
        ) : (
          <main>
            <Breadcrumb items={breadcrumbItems} />

            {/* Manga Info Hero */}
            <section className="py-10 md:py-16 bg-background-50">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                    <div className="w-full lg:w-1/4 flex-shrink-0">
                      <div className="rounded-2xl overflow-hidden aspect-[3/4] max-w-[280px] mx-auto">
                        <img
                          src={manga.cover}
                          alt={manga.title}
                          title={`${manga.title} - 178漫画网`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    <div className="w-full lg:w-3/4">
                      <h1 className="text-2xl md:text-4xl font-heading text-foreground-900">
                        {manga.title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-3 mt-4">
                        <span className="text-sm text-foreground-500">作者：{manga.author}</span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          manga.status === '连载中'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-foreground-200 text-foreground-700'
                        }`}>
                          {manga.status}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-accent-600">
                          <i className="ri-star-fill"></i>
                          {manga.rating}
                        </span>
                        <span className="text-sm text-foreground-400">
                          <i className="ri-eye-line mr-1"></i>
                          {(manga.views / 10000).toFixed(1)}万阅读
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {mangaCategories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/category/${cat.slug}`}
                            className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                      <p className="mt-6 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                        {manga.description}
                      </p>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-8">
                        <button
                          onClick={handleStartReading}
                          className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer inline-flex items-center justify-center gap-2"
                        >
                          <i className="ri-play-circle-line"></i>
                          开始阅读
                        </button>
                        <button
                          onClick={() => {
                            if (chapterList.length > 0) {
                              handleSelectChapter(chapterList[0]);
                              setIsReading(true);
                            }
                          }}
                          className="px-8 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-50 transition-colors cursor-pointer"
                        >
                          从最新话开始
                        </button>
                      </div>

                      {/* Chapter List */}
                      <div className="mt-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-foreground-800">
                            章节列表
                            <span className="text-sm font-normal text-foreground-400 ml-2">
                              共 {chapterList.length} 话
                            </span>
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto pr-2">
                          {chapterList.map((ch) => (
                            <button
                              key={ch.id}
                              onClick={() => {
                                setSelectedChapter(ch);
                                const content = getChapterContent(ch.id);
                                setChapterContent(content);
                                setIsReading(true);
                              }}
                              className="flex items-center justify-between px-4 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer text-left whitespace-nowrap"
                            >
                              <span className="truncate">{ch.title}</span>
                              <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{ch.updateDate}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Manga */}
            {relatedManga.length > 0 && (
              <section className="py-12 md:py-16 bg-background-100">
                <div className="w-full px-4 md:px-6">
                  <div className="max-w-7xl mx-auto">
                    <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                      相关推荐
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                      {relatedManga.map((m) => (
                        <MangaCard key={m.id} manga={m} variant="default" />
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </main>
        )}

        <Footer />
      </div>
    </>
  );
}